import * as React from "react";
import useQuery from "hooks/useQuery";
import { User, UserRole, UserUpdateModel } from "model/User";
import * as S from "@styled";
import ContainerWithLoadingSpinner from "components/shared/ContainerWithLoadingSpinner";
import Dropdown from "components/shared/Dropdown";
import { SaveButton } from "components/shared/Button";
import Modal, { ModalProps } from "components/shared/Modal";
import RadioGroup from "components/shared/RadioGroup";
import Input from "components/shared/Input";
import { OrganizationBase } from "model/Organization";
import { DropdownOption } from "@frends/ui-components/dist/types/dropdown/Dropdown";
import useDeferredMutation from "hooks/useDeferredMutation";
import { queryClient } from "index";
import Link from "components/shared/Link";
import Text from "components/shared/Text";
import { RadioGroupOption } from "@frends/ui-components/dist/react/types/react-wrappers/src/RadioGroup";

interface EditUserModalProps extends ModalProps {
    user: User;
    portalRole: UserRole;
    canSelectOrganizations: boolean;
    onUserEdited: () => void;
}

const EditUserModal: React.FC<EditUserModalProps> = (props) => {
    const [user, setUser] = React.useState(props.user);
    const [selectedRole, setSelectedRole] = React.useState(props.portalRole);
    const [confirmUserDeleteOpenForUserId, setConfirmUserDeleteOpenForUserId] =
        React.useState<number>();
    const orgIdentifiersSetRef = React.useRef(new Set<number>());

    const registerOrgChange = (id: number) => {
        orgIdentifiersSetRef.current.add(id);
    };
    const cleanOrgChanges = () => {
        orgIdentifiersSetRef.current.clear();
    };

    const invalidateQueries = () => {
        queryClient.invalidateQueries({ queryKey: ["users"] });

        orgIdentifiersSetRef.current.forEach((id) => {
            queryClient.invalidateQueries({
                queryKey: [`users/organization/${id}`],
            });
        });

        cleanOrgChanges();
    };

    const organizationsFetch = useQuery<OrganizationBase[]>(
        "organizations",
        undefined,
        { enabled: !!props.canSelectOrganizations },
        "Failed to fetch organizations.",
    );

    const update = useDeferredMutation(
        "PUT",
        invalidateQueries,
        "Failed to update user.",
    );

    const updateRoles = useDeferredMutation(
        "PUT",
        () => {
            invalidateQueries();
            props.onClose?.();
        },
        "Failed to update user roles.",
    );

    const rolesOptions = () => {
        return [
            { label: "User", value: UserRole.User.toString() },
            { label: "Portal Admin", value: UserRole.TenantAdmin.toString() },
        ] as RadioGroupOption[];
    };

    const organizationOptions: DropdownOption[] = React.useMemo(() => {
        if (!organizationsFetch.data) {
            return [];
        }

        const allOptions = organizationsFetch.data.map((org) => ({
            label: org.name,
            value: org.id,
        }));

        return allOptions;
    }, [organizationsFetch.data, selectedRole]);

    const del = useDeferredMutation(
        "DELETE",
        invalidateQueries,
        "Failed to delete user.",
    );

    const handleUserUpdate = (id: number, model: UserUpdateModel) => {
        update.mutate({
            url: `users/${id}`,
            body: model,
        });

        setUser((prevUser) => ({
            ...prevUser,
            disabled: model.disabled as boolean,
        }));
    };

    const handleUserDisabledChange = (id: number, disabled: boolean) =>
        handleUserUpdate(id, { disabled });

    const handleDeleteUserClick = (id: number) =>
        setConfirmUserDeleteOpenForUserId(id);

    const handleDeleteUserConfirmedClick = (id: number) => {
        setConfirmUserDeleteOpenForUserId(undefined);
        del.mutate({ url: `users/${id}` });
        props.onClose?.();
    };

    const handleOrganizationsChange = (organizations: string[]) => {
        const nameSet = new Set(organizations);
        const localRoles = user.roles;
        for (let i = user.roles.length - 1; i >= 0; i--) {
            if (!nameSet.has(user.roles[i].organizationId.toString())) {
                registerOrgChange(user.roles[i].organizationId);
                localRoles.splice(i, 1);
            }
        }
        organizations.forEach((orgId) => {
            if (
                !localRoles.some(
                    (orgRole) => orgRole.organizationId.toString() === orgId,
                )
            ) {
                localRoles.push({
                    roleName: selectedRole,
                    organizationId: Number.parseInt(orgId),
                    organizationName: organizationOptions.find(
                        (org) => org.value === Number.parseInt(orgId),
                    )?.label as string,
                });
                registerOrgChange(Number.parseInt(orgId));
            }
        });

        setUser((prevUser) => ({ ...prevUser, roles: localRoles }));
    };

    const handleRoleChange = (newRole: UserRole) => {
        setSelectedRole(newRole as UserRole);
        const nextRoles = user.roles.map((r) => ({ ...r, roleName: newRole }));
        setUser((prev) => ({ ...prev, roles: nextRoles }));
    };

    const handleSaveButtonClick = () => {
        updateRoles.mutate({
            url: `users/${user.id}/roles`,
            body: user.roles,
        });
    };

    return (
        <Modal title="Edit user" open={props.open} onClose={props.onClose}>
            <ContainerWithLoadingSpinner
                loading={
                    organizationsFetch.isLoading ||
                    update.isLoading ||
                    del.isLoading
                }
            >
                <S.EditGlobalUserModalForm>
                    <Input
                        label="Email"
                        type="text"
                        value={user.name}
                        readonly
                    />
                    <Dropdown
                        label="Organizations"
                        placeholder="Select organizations"
                        multivalue
                        searchable
                        options={organizationOptions}
                        selected={user.roles.map((r) => r.organizationId)}
                        onChange={handleOrganizationsChange}
                    />
                    <RadioGroup
                        title="In Role"
                        name="role"
                        options={rolesOptions()}
                        select={selectedRole}
                        onChange={handleRoleChange}
                    />
                    <div>
                        <Link
                            type="button"
                            onClick={() =>
                                handleUserDisabledChange(
                                    user.id,
                                    !user.disabled,
                                )
                            }
                        >
                            {user.disabled
                                ? "Activate account"
                                : "Disable account"}
                        </Link>
                    </div>
                    <div>
                        {confirmUserDeleteOpenForUserId == undefined && (
                            <Link
                                type="button"
                                onClick={() => handleDeleteUserClick(user.id)}
                            >
                                Delete account
                            </Link>
                        )}
                        {confirmUserDeleteOpenForUserId != undefined && (
                            <div>
                                <Text>
                                    This cannot be undone. Please Confirm.
                                </Text>
                                <Link
                                    type="button"
                                    onClick={() =>
                                        handleDeleteUserConfirmedClick(
                                            confirmUserDeleteOpenForUserId,
                                        )
                                    }
                                >
                                    Yes, Delete
                                </Link>
                                <Link
                                    type="button"
                                    onClick={() =>
                                        setConfirmUserDeleteOpenForUserId(
                                            undefined,
                                        )
                                    }
                                >
                                    Cancel
                                </Link>
                            </div>
                        )}
                    </div>
                    <div>
                        <SaveButton
                            type="button"
                            variant="bordered"
                            loading={updateRoles.isLoading}
                            error={!!updateRoles.error}
                            onClick={handleSaveButtonClick}
                        >
                            Save Role Changes
                        </SaveButton>
                    </div>
                </S.EditGlobalUserModalForm>
            </ContainerWithLoadingSpinner>
        </Modal>
    );
};
export default EditUserModal;
